import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";

import { getMunicipalityStats } from "../../../shared/api";
import MunicipalityCard from "./municipalityCard";
import municipalitiesCentroidsJson from "../../shared/municipalities_centroids.json";
import { useClient } from "../../../contexts/ClientContext";
import { isPortfolioIdRequiredError } from "./portfolioApiError";

function MunicipalitiesView({
  onMunicipalitySelect,
  mapRef,
  selectedPortfolioId,
}) {
  const { t } = useTranslation();
  const { clientInfo } = useClient();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orderCombined, setOrderCombined] = useState("properties_count:desc");
  const searchTimeoutRef = useRef(null);

  // Debounce search input - only debounce the actual search, not the input display
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  const {
    data: municipalitiesData,
    isLoading: loadingMunicipalities,
    error,
    refetch,
  } = useQuery({
    queryKey: ["municipality-stats", clientInfo?.clientId, selectedPortfolioId],
    queryFn: () =>
      getMunicipalityStats({
        clientId: clientInfo?.clientId,
        portfolioId: selectedPortfolioId,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    enabled: !!clientInfo?.clientId && !!selectedPortfolioId,
  });

  const municipalities = useMemo(
    () =>
      municipalitiesData?.items?.filter((m) => m.properties_count > 0) ?? [],
    [municipalitiesData],
  );

  // Memoize the search term to avoid unnecessary recalculations
  const searchTerm = useMemo(
    () => debouncedSearch.trim().toLowerCase(),
    [debouncedSearch],
  );

  // Memoize the filtered municipalities separately
  const filteredMunicipalities = useMemo(() => {
    if (!searchTerm) return municipalities;

    return municipalities.filter((m) =>
      String(m["name:en"] || m.municipality || "")
        .toLowerCase()
        .includes(searchTerm),
    );
  }, [municipalities, searchTerm]);

  // Memoize the sorted municipalities
  const orderedMunicipalities = useMemo(() => {
    const [key, dirStr] = orderCombined.split(":");
    const dir = dirStr === "asc" ? 1 : -1;
    const numericKeys = new Set([
      "properties_count",
      "total_capital_at_risk",
      "total_contract_price",
      "avg_flood_risk",
      "avg_fire_risk",
      "avg_earthquake_risk",
    ]);

    return [...filteredMunicipalities].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (numericKeys.has(key)) {
        const an = Number(av ?? 0);
        const bn = Number(bv ?? 0);
        if (an === bn) return 0;
        return an > bn ? dir : -dir;
      }
      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as === bs) return 0;
      return as > bs ? dir : -dir;
    });
  }, [filteredMunicipalities, orderCombined]);

  const totalMunicipalities = useMemo(() => {
    return orderedMunicipalities?.length ?? 0;
  }, [orderedMunicipalities]);

  const handleMunicipalityClick = useCallback(
    (m) => {
      onMunicipalitySelect?.({
        id: m.id,
        name: m["name:en"] || m.municipality,
      });

      const centroid = municipalitiesCentroidsJson.find((c) => c.id === m.id);
      const lng = centroid?.centroid_lon;
      const lat = centroid?.centroid_lat;
      if (mapRef?.current && lng != null && lat != null) {
        try {
          mapRef.current.flyTo({
            center: [Number(lng), Number(lat)],
            zoom: 9,
          });
        } catch {}
      }
    },
    [onMunicipalitySelect, mapRef],
  );

  // Memoize the input change handler to prevent re-renders
  const handleSearchInputChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleOrderChange = useCallback((e) => {
    setOrderCombined(e.target.value);
  }, []);

  const orderOptions = useMemo(
    () => [
      {
        value: "properties_count:desc",
        label: t("portfolioMap.order.propertiesCountDesc"),
      },
      {
        value: "properties_count:asc",
        label: t("portfolioMap.order.propertiesCountAsc"),
      },
      {
        value: "total_capital_at_risk:desc",
        label: t("portfolioMap.order.totalCapitalAtRiskDesc"),
      },
      {
        value: "total_capital_at_risk:asc",
        label: t("portfolioMap.order.totalCapitalAtRiskAsc"),
      },
      {
        value: "total_contract_price:desc",
        label: t("portfolioMap.order.totalContractPriceDesc"),
      },
      {
        value: "total_contract_price:asc",
        label: t("portfolioMap.order.totalContractPriceAsc"),
      },
      {
        value: "avg_flood_risk:desc",
        label: t("portfolioMap.order.avgFloodRiskDesc"),
      },
      {
        value: "avg_flood_risk:asc",
        label: t("portfolioMap.order.avgFloodRiskAsc"),
      },
      {
        value: "avg_fire_risk:desc",
        label: t("portfolioMap.order.avgFireRiskDesc"),
      },
      {
        value: "avg_fire_risk:asc",
        label: t("portfolioMap.order.avgFireRiskAsc"),
      },
      {
        value: "avg_earthquake_risk:desc",
        label: t("portfolioMap.order.avgEarthquakeRiskDesc"),
      },
      {
        value: "avg_earthquake_risk:asc",
        label: t("portfolioMap.order.avgEarthquakeRiskAsc"),
      },
    ],
    [t],
  );

  if (!selectedPortfolioId) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("portfolioMap.noPortfolioFound", {
          defaultValue: "No portfolio found for this client yet.",
        })}
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t("portfolioMap.searchByName")}
          value={searchInput}
          onChange={handleSearchInputChange}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t("common.order")}
          value={orderCombined}
          onChange={handleOrderChange}
        >
          {orderOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Total Count Display */}
      {!loadingMunicipalities && totalMunicipalities > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, textAlign: "right" }}
        >
          {t("portfolioMap.resultsCount", { count: totalMunicipalities })}
        </Typography>
      )}

      <Stack gap={1.5}>
        {error && (
          <Stack gap={1}>
            <Typography color="error">
              {isPortfolioIdRequiredError(error)
                ? t("portfolioMap.errors.portfolioRequired", {
                    defaultValue:
                      "A valid portfolio is required. Please select a completed portfolio.",
                  })
                : t("portfolioMap.errors.loadFailed")}
            </Typography>
            <Button variant="contained" onClick={() => refetch()}>
              {t("common.tryAgain")}
            </Button>
          </Stack>
        )}

        {loadingMunicipalities &&
          Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={`sk-m-${i}`}
              p={1}
              borderRadius={2}
              border="1px solid #eee"
            >
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          ))}

        {!error &&
          orderedMunicipalities.map((m) => (
            <MunicipalityCard
              key={m.id}
              name={m["name:en"] || m.municipality}
              propertiesCount={m.properties_count}
              totalCapitalAtRisk={m.total_capital_at_risk}
              totalContractPrice={m.total_contract_price}
              avgFloodRisk={m.avg_flood_risk}
              avgFireRisk={m.avg_fire_risk}
              avgEarthquakeRisk={m.avg_earthquake_risk}
              fireBin={m.fire_bin}
              floodBin={m.flood_bin}
              earthquakeBin={m.earthquake_bin}
              onClick={() => handleMunicipalityClick(m)}
            />
          ))}

        {!error &&
          !loadingMunicipalities &&
          orderedMunicipalities.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t("portfolioMap.noMunicipalities")}
            </Typography>
          )}
      </Stack>
    </>
  );
}

export default MunicipalitiesView;
